/**
 * OnboardingView — the onboarding agent's surface (FLO-51).
 *
 * Replaces the bespoke `TranscriptSidebar` panel with the SAME chat surface the
 * website agent uses ({@link WebsiteAgentChat}), so the two agents share one set
 * of controls, one set of button positions, and one set of behaviours. What is
 * deliberately NOT shared is the geometry:
 *
 *   website agent (floating) — a centered `aria-modal` card over a dimmed,
 *     click-to-dismiss backdrop. Correct for a marketing page, where taking over
 *     the screen IS the interaction.
 *   onboarding (here)        — a DOCKED bottom-right card, no backdrop, nothing
 *     blocking. The onboarding agent drives the host page: the user has to see
 *     and click the product while being guided, so a backdrop over the product
 *     would break the one thing this agent exists to do.
 *
 * The closed state stays {@link ControlHub} — the orb plus its nudge, including
 * the impression/cooldown persistence that lives there. Clicking either the orb
 * or the nudge is the single gesture that opens the panel AND starts the
 * session: it is a real user activation, which is what lets the mic prompt
 * appear on iOS Safari (FLO-109). Nothing auto-connects on page load.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ControlHub } from './ControlHub';
import { WebsiteAgentChat } from './WebsiteAgentChat';
import { AgentState, ConnectionProgress, TranscriptMessage } from '../types/agent';
import type { PlanProgress } from '../sdk/AgentUI';

/** Matches the website agent's coral so both surfaces share one unset-fallback. */
const DEFAULT_ACCENT = '#FF5C35';

export interface OnboardingViewProps {
  connected: boolean;
  agentState: AgentState;
  /** Mic (voice input) state. */
  voiceEnabled: boolean;
  botTranscript: string;
  userTranscript: string;
  transcriptHistory: TranscriptMessage[];
  connectionProgress?: ConnectionProgress;
  fatalError?: string;
  /** Mic mute/unmute. */
  onToggleMute?: () => void;
  /** The browser refused the mic — the control has to say so (FLO-51). */
  micPermissionDenied?: boolean;
  /** The mic request failed because no usable input device was available. */
  micUnavailable?: boolean;
  /** Bot-voice mute. Independent of the mic and of typing (FLO-51). */
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  onSendText?: (text: string) => void;
  /** Screen share — only supplied when the site enabled screen capture. */
  screenShareEnabled?: boolean;
  onToggleScreenShare?: () => void;
  /**
   * Boots the session. MUST run synchronously from the orb/nudge click so the
   * iOS mic prompt lands inside the transient user activation. Idempotent on
   * the SDK side.
   */
  onStartSession: () => void;
  /** Ends the session (tears down pipecat). Lands on the friendly ended state. */
  onEndSession?: () => void;
  /** Resume an idle-PAUSED session. Absent when there is nothing to resume. */
  onResume?: () => void;
  /** Collapse to the orb — the session stays live. */
  onMinimize?: () => void;
  onExpand?: () => void;
  /** Onboarding-only step counter. */
  planProgress?: PlanProgress;
  /** Server-pushed suggestion chips (the `suggested_options` channel). */
  suggestedOptions?: string[];
  // ─── closed-state (ControlHub) inputs ──────────────────────────────────────
  isReturningUser?: boolean;
  /**
   * FLO-534 `activation: 'on_demand'`. Changes what the agent OPENS with: a
   * support agent answers the question you came with, it does not pitch a
   * setup walkthrough. Mirrors the Shepherd's own support-mode prompt section
   * so the first bubble and the first spoken turn agree.
   */
  supportMode?: boolean;
  nudgeText?: string;
  showNudge?: boolean;
  onNudgeDismiss?: () => void;
  nudgeAutoHideDelay?: number;
  nudgeAutoShow?: boolean;
  // ─── branding ──────────────────────────────────────────────────────────────
  siteName?: string;
  siteLogoUrl?: string;
  siteFaviconUrl?: string;
  accentColor?: string;
  // ─── FLO-585: launcher corner + inset ──────────────────────────────────────
  launcherPosition?: 'bottom-right' | 'bottom-left';
  launcherInset?: number;
  /**
   * FLO-545: the live avatar track (Anam egress participant `floe-avatar`).
   * Onboarding has no pre-join avatar config, so the stream's presence is the
   * enable signal — forwarded into WebsiteAgentChat's hero slot, where a live
   * stream always wins over static media.
   */
  avatarVideoStream?: MediaStream | null;
  /**
   * Whether this session has an avatar at all. Previously derived from the
   * stream's presence, which meant a dropped track (idle swap, mid-session
   * teardown) took the entire face surface with it instead of falling back.
   */
  avatarEnabled?: boolean;
  /** Site idle loop + still, shown when the live track is absent. */
  avatarIdleLoopUrl?: string;
  avatarPosterUrl?: string;
}

export const OnboardingView: React.FC<OnboardingViewProps> = ({
  connected,
  agentState,
  voiceEnabled,
  botTranscript,
  userTranscript,
  transcriptHistory,
  connectionProgress,
  fatalError,
  onToggleMute,
  micPermissionDenied,
  micUnavailable,
  speakerMuted,
  onToggleSpeaker,
  onSendText,
  screenShareEnabled,
  onToggleScreenShare,
  onStartSession,
  onEndSession,
  onResume,
  onMinimize,
  onExpand,
  planProgress,
  suggestedOptions,
  isReturningUser = false,
  supportMode = false,
  nudgeText,
  showNudge,
  onNudgeDismiss,
  nudgeAutoHideDelay,
  nudgeAutoShow,
  siteName,
  siteLogoUrl,
  siteFaviconUrl,
  accentColor,
  launcherPosition,
  launcherInset,
  avatarVideoStream,
  avatarEnabled,
  avatarIdleLoopUrl,
  avatarPosterUrl,
}) => {
  // Closed (orb) vs open (docked panel). Starts CLOSED for everyone: FLO-51
  // removed the page-load welcome modal, so the first click is what opens the
  // panel and starts the session. A session that is already live re-opens it
  // (below) — e.g. a reconnect, or a host page calling connect() directly.
  const [panelOpen, setPanelOpen] = useState(false);
  const [ended, setEnded] = useState(false);

  // A live session always shows its panel. This is also what makes an
  // SDK-initiated connect (public `connect()` API, resume) surface the UI
  // without the caller having to drive open-state from outside.
  useEffect(() => {
    if (connected) {
      setPanelOpen(true);
      setEnded(false);
    }
  }, [connected]);

  const handleOpen = useCallback(() => {
    setEnded(false);
    setPanelOpen(true);
    onExpand?.();
    // Only START when there is nothing running. Re-opening a minimized session
    // is not a request for a new one (the SDK no-ops too — belt and braces,
    // because getting this wrong hangs up on the user).
    // Called synchronously inside the click: the iOS mic prompt depends on the
    // transient user activation surviving to getUserMedia (FLO-109).
    if (!connected) onStartSession();
  }, [connected, onExpand, onStartSession]);

  const handleMinimize = useCallback(() => {
    setPanelOpen(false);
    onMinimize?.();
  }, [onMinimize]);

  // End = tear the session down AND stay open on the friendly ended state, so
  // "end" and "collapse" are visibly different outcomes. That distinction is
  // the whole point of FLO-51's header: today's `—` and `⏻` look identical and
  // only one of them stops the (billed) session.
  const handleEndSession = useCallback(() => {
    onEndSession?.();
    setEnded(true);
  }, [onEndSession]);

  const handleRestart = useCallback(() => {
    setEnded(false);
    onStartSession();
  }, [onStartSession]);

  const { currentTranscript, currentRole } = useMemo(() => {
    if (agentState === 'speaking' && botTranscript) {
      return { currentTranscript: botTranscript, currentRole: 'assistant' as const };
    }
    if (agentState === 'listening' && userTranscript) {
      return { currentTranscript: userTranscript, currentRole: 'user' as const };
    }
    return { currentTranscript: '', currentRole: undefined };
  }, [agentState, botTranscript, userTranscript]);

  // The idle-timeout pause reuses the ended card: same shape (a dead end plus
  // one button back in), different words. A second full-panel state would just
  // drift from this one.
  const paused = !ended && agentState === 'paused';

  const accent = accentColor?.trim() || DEFAULT_ACCENT;
  const product = siteName?.trim();

  // FLO-585: the panel anchors to the same corner as the launcher so the widget
  // doesn't jump corners on open. Defaults match the old hardcoded values.
  const panelPosition = launcherPosition === 'bottom-left' ? 'bottom-left' : 'bottom-right';
  const panelInset = typeof launcherInset === 'number' && Number.isFinite(launcherInset)
    ? Math.max(0, Math.round(launcherInset))
    : 24;

  // Onboarding's opening turn is not the website agent's "ask me anything about
  // our product" — the user is already inside the product they signed up for.
  const greeting = useMemo(
    () =>
      supportMode
        ? [
            product ? `Hi 👋 — ${product} support.` : 'Hi 👋',
            'What do you need a hand with? Talk or type, whichever you prefer.',
          ]
        : [
            product ? `Hi 👋 — welcome to ${product}.` : 'Hi 👋 — welcome.',
            'I can walk you through setup, or just answer questions. Talk or type, whichever you prefer.',
          ],
    [product, supportMode],
  );

  // Empty-state chips. Passed explicitly because onboarding/support needs
  // product-use prompts rather than the website agent's contextual/demo offer.
  const openerChips = useMemo(
    () =>
      supportMode
        ? ['I need help with something', 'How does this work?', 'Something is not working']
        : ['Help me get set up', 'What should I do first?', 'Show me around'],
    [supportMode],
  );

  return (
    <>
      {/* Closed: the orb + its nudge. Clicking either opens AND starts. */}
      {!panelOpen && (
        <ControlHub
          connected={connected}
          agentState={agentState}
          onExpand={handleOpen}
          isReturningUser={isReturningUser}
          nudgeText={nudgeText}
          showNudge={showNudge}
          onNudgeDismiss={onNudgeDismiss}
          nudgeAutoHideDelay={nudgeAutoHideDelay}
          nudgeAutoShow={nudgeAutoShow}
          // FLO-585: customer brand + position
          siteName={siteName}
          siteLogoUrl={siteLogoUrl}
          siteFaviconUrl={siteFaviconUrl}
          accent={accent}
          position={launcherPosition}
          inset={launcherInset}
        />
      )}

      {/* Open: a DOCKED card. No backdrop, no `aria-modal` — the host page
          stays fully interactive, because the agent is about to drive it. */}
      {panelOpen && (
        <>
          <style>{`
            @keyframes floe-ob-pop { from { opacity: 0; transform: translateY(12px) scale(0.985); } to { opacity: 1; transform: none; } }
            @media (prefers-reduced-motion: reduce) { .floe-ob-panel { animation: none !important; } }
          `}</style>
          <div
            className="floe-ob-panel"
            role="complementary"
            aria-label={product ? `${product} onboarding assistant` : 'Floe onboarding assistant'}
            style={{
              position: 'fixed',
              bottom: panelInset,
              ...(panelPosition === 'bottom-left'
                ? { left: panelInset }
                : { right: panelInset }),
              width: 'min(400px, calc(100vw - 32px))',
              height: 'min(72vh, 620px)',
              maxHeight: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 20,
              boxShadow: '0 32px 80px -20px rgba(20,18,16,0.42), 0 6px 20px rgba(20,18,16,0.14)',
              background: '#FFFFFF',
              zIndex: 2147483645,
              pointerEvents: 'auto',
              animation: 'floe-ob-pop 0.32s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <WebsiteAgentChat
              // `docked` drops the dialog/aria-modal semantics — true here for
              // the same reason it is true of the website agent's rail: nothing
              // behind this panel is inert.
              docked
              connected={connected}
              agentState={agentState}
              messages={transcriptHistory}
              currentTranscript={currentTranscript}
              currentRole={currentRole}
              voiceEnabled={voiceEnabled}
              onToggleMute={onToggleMute}
              micPermissionDenied={micPermissionDenied}
              micUnavailable={micUnavailable}
              speakerMuted={speakerMuted}
              onToggleSpeaker={onToggleSpeaker}
              screenShareEnabled={screenShareEnabled}
              onToggleScreenShare={onToggleScreenShare}
              onSendText={onSendText}
              onMinimize={handleMinimize}
              onEndSession={onEndSession ? handleEndSession : undefined}
              ended={ended || paused}
              onRestart={paused ? (onResume ?? handleRestart) : handleRestart}
              endedTitle={paused ? 'Session paused' : undefined}
              endedBody={paused ? 'You were inactive for a bit — pick up where you left off.' : undefined}
              endedCta={paused ? 'Resume' : undefined}
              connectionProgress={connectionProgress}
              fatalError={fatalError}
              planProgress={planProgress}
              greeting={greeting}
              composerPlaceholder="Ask, or tell me what you want to set up…"
              openerChips={openerChips}
              followUpOptions={suggestedOptions}
              siteName={siteName}
              siteLogoUrl={siteLogoUrl}
              siteFaviconUrl={siteFaviconUrl}
              accent={accent}
              avatarEnabled={avatarEnabled ?? !!avatarVideoStream}
              avatarVideoStream={avatarVideoStream}
              avatarMedia={{ idleLoopUrl: avatarIdleLoopUrl, posterUrl: avatarPosterUrl }}
            />
          </div>
        </>
      )}
    </>
  );
};
